import * as Sentry from '@sentry/vue';
import { i18n } from '@/plugins/i18n';
import { ApiError, ApiErrorBody } from '@/interfaces/apiError';

const reportedUnknownCodes = new Set<string>();
const reportedUncodedErrors = new Set<string>();

// Codes are used as i18n keys, so a `.` would be read as a nested path.
const isValidCode = (code: unknown): code is string =>
  typeof code === 'string' && code.length > 0 && !code.includes('.');

// Checks the fallback locale too: `te` only looks at the current locale by default, while
// `t` itself does fall back, so a code translated only in `nl` is still a known code.
const hasTranslation = (key: string): boolean =>
  i18n.te(key) || i18n.te(key, String(i18n.fallbackLocale));

// Returns the translated message for a known backend error code, or undefined when the
// body carries no code or the code has no translation.
export function resolveApiErrorMessage(body: ApiErrorBody | undefined): string | undefined {
  const code = body?.code;
  if (!isValidCode(code)) return undefined;

  const key = `errors.codes.${code}`;
  if (!hasTranslation(key)) return undefined;

  return String(i18n.t(key, body?.params ?? {}));
}

// Flags a backend error code the frontend has no translation for yet, once per code per session.
export function reportUnknownErrorCode(code: string, status?: number, url?: string): void {
  if (reportedUnknownCodes.has(code)) return;
  reportedUnknownCodes.add(code);

  Sentry.captureMessage('Unknown backend error code', {
    level: 'warning',
    tags: { api_error_code: code },
    fingerprint: ['unknown-api-error-code', code],
    extra: { status, url },
  });
}

const ID_SEGMENT = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

// Collapses a request URL to a stable template (`patients/42/notes?x=1` -> `patients/:id/notes`)
// so the same endpoint always yields the same fingerprint.
export function toUrlTemplate(url?: string): string {
  if (!url) return 'unknown';
  const path = url.split(/[?#]/)[0].replace(/^https?:\/\/[^/]+/i, '');
  return path.split('/').map((s) => (ID_SEGMENT.test(s) ? ':id' : s)).join('/');
}

// Flags a 4xx response that carries no `code`, once per status + endpoint per session. The
// fingerprint deliberately excludes the `detail` text, which varies per request and would
// fragment one endpoint into many Sentry issues.
export function reportUncodedApiError(
  status: number | undefined,
  method: string | undefined,
  urlTemplate: string,
  detail?: unknown,
): void {
  const key = `${status}|${urlTemplate}`;
  if (reportedUncodedErrors.has(key)) return;
  reportedUncodedErrors.add(key);

  Sentry.captureMessage('API 4xx without error code', {
    level: 'warning',
    tags: { source: 'uncoded-4xx', http_status: String(status) },
    fingerprint: ['uncoded-api-error', String(status), urlTemplate],
    extra: { status, method: method?.toUpperCase(), urlTemplate, detail },
  });
}

// Called by the response interceptor for 4xx responses. Attaches `errorCode` and
// `messageI18n` when the backend sends a code. Without a code, the error is left untouched
// but reported to Sentry (unless the request set `silentApiError`).
export function enrichApiError(error: ApiError): void {
  const code = error.response?.data?.code;
  if (typeof code !== 'string' || !code) {
    if (!error.config?.silentApiError) {
      reportUncodedApiError(
        error.response?.status,
        error.config?.method,
        toUrlTemplate(error.config?.url),
        error.response?.data?.detail,
      );
    }
    return;
  }

  error.errorCode = code;
  error.messageI18n = resolveApiErrorMessage(error.response?.data);

  if (error.messageI18n === undefined) {
    reportUnknownErrorCode(code, error.response?.status, error.config?.url);
  }
}

// User-facing message for a backend 4xx error. Prefers the translated message for a known
// code; otherwise falls back to the existing untranslated `detail` text.
export function translateApiError(error: ApiError, fallbackKey = 'common.errorOccurred'): string {
  if (error.messageI18n) return error.messageI18n;

  const body = error.response?.data;
  const translated = resolveApiErrorMessage(body);
  if (translated) return translated;

  const detail = body?.detail;
  if (typeof detail === 'string' && detail) {
    return detail;
  }

  if (Array.isArray(detail) && detail.length > 0 && typeof detail[0]?.msg === 'string') {
    return detail[0].msg;
  }

  return String(i18n.t(fallbackKey));
}

// Test-only: clears the per-session dedupe of reported unknown codes.
export function resetReportedUnknownCodes(): void {
  reportedUnknownCodes.clear();
  reportedUncodedErrors.clear();
}
